import { useState, useCallback, useRef, useEffect } from "react";
import apiClient from "../utils/apiClient";
import { usePlan } from "../contexts/PlanContext";
import { useSoftphoneContext } from "../contexts/SoftphoneContext";

/**
 * Click-to-call from anywhere in the CRM.
 *
 * The institute connects their own telephony provider; we ask it to ring the
 * counsellor's own phone first, and once they answer it dials the student.
 * Neither side sees the other's personal number.
 *
 * Two things this hook exists to get right in one place:
 *
 *  • **The provider's 200 is an accept, not a call.** The row comes back
 *    `queued` and only a webhook resolves it a few seconds later. So we poll
 *    briefly rather than telling the user "done" and being wrong.
 *  • **Graceful fallback.** When the plan doesn't include telephony, or nothing
 *    is configured, the caller should fall back to a plain `tel:` link instead
 *    of showing a dead button. `enabled` says which world we're in.
 *  • **Mode fallback.** In browser mode the call rings a SIP endpoint instead of
 *    the counsellor's mobile — but only while that endpoint is genuinely
 *    registered. If the softphone isn't live (mic denied, SDK missing, offline,
 *    mid-retry) this hook asks the server for phone mode explicitly rather than
 *    letting the call ring a browser that cannot answer it.
 *
 * Usage:
 *   const { enabled, calling, activeCall, placeCall, dismiss } = useClickToCall();
 *   enabled ? <button onClick={() => placeCall({ enquiryId })}/> : <a href={`tel:${m}`}/>
 */

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 90_000;

export default function useClickToCall({ onLogged } = {}) {
  const { hasPlanFeature } = usePlan();
  const enabled = hasPlanFeature("cloudTelephony");
  // Safe outside the provider: the context defaults to a phone-mode object rather
  // than undefined, so a component mounted without it still dials.
  const { effectiveMode } = useSoftphoneContext();

  const [calling, setCalling] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [error, setError] = useState("");

  const pollRef = useRef(null);
  const stoppedAtRef = useRef(0);
  const inFlightRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Never leave an interval running after the component goes away.
  useEffect(() => stopPolling, [stopPolling]);

  const dismiss = useCallback(() => {
    stopPolling();
    setActiveCall(null);
    setError("");
  }, [stopPolling]);

  const placeCall = useCallback(async ({ enquiryId, studentId, toNumber } = {}) => {
    if (!enabled) return null;
    // Hard guard, not just a disabled button. Every dispatch spends the
    // institute's real money at their provider, and a double-click on a slow
    // connection would place two genuine calls to the same student.
    if (inFlightRef.current) return null;
    inFlightRef.current = true;

    setCalling(true);
    setError("");
    try {
      const { data } = await apiClient.post("/telephony/calls", {
        enquiryId,
        studentId,
        toNumber,
        // Only ever sent as a downgrade. The server decides whether browser mode
        // is allowed at all — a browser cannot grant itself a SIP identity — but
        // it cannot see that this tab's registration just dropped, so that one
        // fact has to travel with the request.
        ...(effectiveMode === "phone" ? { callMode: "phone" } : {}),
      });

      setActiveCall({
        id: data.callId,
        status: data.status,
        message: data.message,
        whoRingsFirst: data.whoRingsFirst,
        callMode: data.callMode,
      });

      // Poll for the real outcome. Bounded, because a call whose webhook never
      // arrives must not leave a spinner running forever — the reconcile cron
      // will settle the record server-side either way.
      stopPolling();
      stoppedAtRef.current = Date.now() + POLL_TIMEOUT_MS;
      pollRef.current = setInterval(async () => {
        if (Date.now() > stoppedAtRef.current) {
          stopPolling();
          return;
        }
        try {
          const { data: call } = await apiClient.get(`/telephony/calls/${data.callId}`);
          setActiveCall((prev) => (prev ? { ...prev, status: call.status, talkTimeSec: call.talkTimeSec } : prev));
          if (!["queued", "ringing", "in-progress"].includes(call.status)) {
            stopPolling();
            onLogged?.(call);
          }
        } catch {
          /* transient — keep polling until the timeout */
        }
      }, POLL_INTERVAL_MS);

      return data;
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Could not place the call.";
      setError(msg);
      setActiveCall(null);
      return null;
    } finally {
      setCalling(false);
      inFlightRef.current = false;
    }
  }, [enabled, effectiveMode, onLogged, stopPolling]);

  return { enabled, calling, activeCall, error, placeCall, dismiss };
}
